<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <header class="relative bg-[#0F172A] overflow-hidden">
      <!-- Background Effects -->
      <div class="absolute top-0 left-1/2 w-full -translate-x-1/2 h-full z-0 pointer-events-none">
        <div class="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl mix-blend-screen opacity-50"></div>
        <div class="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl mix-blend-screen opacity-50"></div>
        <!-- Grid Pattern -->
        <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-20"></div>
      </div>

      <!-- Navigation -->
      <nav class="relative z-10 container mx-auto px-6 py-4">
        <div class="flex items-center justify-between">
          <router-link to="/" class="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <img src="@/assets/logo.svg" alt="Logo" class="h-10 w-auto" />
            <span class="text-xl font-bold text-white tracking-tight">TrackFolio</span>
          </router-link>
          <div class="hidden md:flex items-center space-x-8">
            <a href="#" class="text-sm font-medium text-gray-300 hover:text-white transition-colors">Home</a>
            <a href="#features" class="text-sm font-medium text-gray-300 hover:text-white transition-colors">Features</a>
            <a href="#pricing" class="text-sm font-medium text-gray-300 hover:text-white transition-colors">Pricing</a>
            <router-link 
              to="/portfolios" 
              class="bg-primary-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-primary-500 transition-all duration-200 shadow-lg shadow-primary-500/25 ring-1 ring-primary-500 ring-offset-2 ring-offset-[#0F172A]"
            >
              Launch App
            </router-link>
          </div>

          <!-- Mobile Menu Button -->
          <button 
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="md:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Toggle mobile menu"
          >
            <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Mobile Menu Overlay -->
        <transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 -translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-2"
        >
          <div 
            v-if="mobileMenuOpen" 
            class="md:hidden absolute top-full left-0 right-0 bg-[#0F172A]/95 backdrop-blur-lg border-t border-white/10 shadow-xl"
          >
            <div class="container mx-auto px-6 py-6 space-y-4">
              <a 
                href="#" 
                @click="mobileMenuOpen = false"
                class="block text-base font-medium text-gray-300 hover:text-white transition-colors py-2"
              >
                Home
              </a>
              <a 
                href="#features" 
                @click="mobileMenuOpen = false"
                class="block text-base font-medium text-gray-300 hover:text-white transition-colors py-2"
              >
                Features
              </a>
              <a 
                href="#pricing" 
                @click="mobileMenuOpen = false"
                class="block text-base font-medium text-gray-300 hover:text-white transition-colors py-2"
              >
                Pricing
              </a>
              <router-link 
                to="/portfolios" 
                @click="mobileMenuOpen = false"
                class="block w-full bg-primary-600 text-white px-5 py-3 rounded-lg font-medium hover:bg-primary-500 transition-all duration-200 text-center mt-4"
              >
                Launch App
              </router-link>
            </div>
          </div>
        </transition>
      </nav>

      <!-- Hero Content -->
      <div class="relative z-10 container mx-auto px-6 pt-8 pb-24 lg:pt-12 lg:pb-32">
        <div class="flex flex-col lg:flex-row items-center lg:items-start gap-12 xl:gap-16">
          <!-- Left Content -->
          <div class="lg:w-1/2 text-center lg:text-left">
            <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6 backdrop-blur-sm">
              <span class="relative flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
              </span>
              <span class="text-sm font-medium text-primary-300">Stop juggling multiple apps</span>
            </div>
            
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight mb-4">
              Retire Your Spreadsheets. <br />
              Master Your <span class="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-cyan-400">Net Worth.</span>
            </h1>
            
            <p class="text-base md:text-lg text-gray-300 mb-6 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Stop wasting hours on manual Excel updates. Track your Stocks, Crypto, and Personal Assets in one automated, professional-grade dashboard.
            </p>
            
            <div class="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <router-link 
                to="/portfolios" 
                class="w-full sm:w-auto px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-500 transition-all duration-200 shadow-xl shadow-primary-500/20 flex items-center justify-center gap-2 group"
              >
                Start Tracking
                <svg class="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </router-link>
              
              <a 
                href="#features" 
                class="w-full sm:w-auto px-8 py-4 bg-white/5 text-white border border-white/10 rounded-xl font-semibold text-lg hover:bg-white/10 transition-all duration-200 flex items-center justify-center backdrop-blur-sm"
              >
                View Features
              </a>
            </div>

            <!-- Social Proof / Stats -->
            <div class="mt-12 pt-8 border-t border-white/5 flex items-center justify-center lg:justify-start gap-8">
              <div>
                <div class="text-2xl font-bold text-white">$2M+</div>
                <div class="text-xs uppercase tracking-wider text-gray-500 mt-1">Assets Tracked</div>
              </div>
              <div class="w-px h-12 bg-white/10"></div>
              <div>
                <div class="text-2xl font-bold text-white">10k+</div>
                <div class="text-xs uppercase tracking-wider text-gray-500 mt-1">Active Users</div>
              </div>
              <div class="w-px h-12 bg-white/10"></div>
              <div>
                <div class="text-2xl font-bold text-white">4.9/5</div>
                <div class="text-xs uppercase tracking-wider text-gray-500 mt-1">User Rating</div>
              </div>
            </div>
          </div>
          
          <!-- Right Image -->
          <div class="lg:w-1/2 relative mt-8 lg:mt-2">
            <div class="absolute inset-0 bg-gradient-to-tr from-primary-500/20 to-cyan-500/20 rounded-3xl blur-2xl transform rotate-3"></div>
            <div class="relative rounded-2xl border border-white/10 bg-gray-900/50 backdrop-blur-xl shadow-2xl p-2 ring-1 ring-white/5">
              <img 
                src="@/assets/hero-image.svg" 
                alt="Dashboard Preview" 
                class="w-full h-auto rounded-xl shadow-lg"
              />
              
              <!-- Floating Badge 1 -->
              <div class="absolute -left-4 md:-left-8 top-1/4 bg-gray-800/90 backdrop-blur-md p-4 rounded-xl border border-gray-700 shadow-xl hidden xl:block animate-bounce-slow">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                    <svg class="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                  </div>
                  <div>
                    <div class="text-xs text-gray-400">Total Profit</div>
                    <div class="text-sm font-bold text-green-400">+$24,500</div>
                  </div>
                </div>
              </div>

              <!-- Floating Badge 2 -->
              <div class="absolute -right-4 md:-right-8 bottom-1/4 bg-gray-800/90 backdrop-blur-md p-4 rounded-xl border border-gray-700 shadow-xl hidden xl:block animate-bounce-slow-delay">
                 <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <svg class="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  </div>
                  <div>
                    <div class="text-xs text-gray-400">Portfolio Value</div>
                    <div class="text-sm font-bold text-white">$142,394</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Bottom Wave Shape -->
      <div class="absolute bottom-0 left-0 right-0 z-10 translate-y-1">
        <svg class="w-full h-12 md:h-24 text-white fill-current" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill-opacity="1" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,261.3C960,256,1056,224,1152,202.7C1248,181,1344,171,1392,165.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>
    </header>

    <!-- Portfolio Overview Section -->
    <section id="features" class="py-20 bg-white">
      <div class="container mx-auto px-6">
        <div class="flex flex-col md:flex-row items-center gap-16">
          <!-- Text Content -->
          <div class="md:w-1/2">
            <div class="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
            </div>
            <h2 class="text-3xl font-bold mb-6 text-gray-900">All Your Assets in One Place</h2>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              From stocks to real estate, track every asset type you own. TrackFolio supports a wide range of investments so you can see your complete financial picture.
            </p>
            
            <!-- Asset Type Tags -->
            <div class="flex flex-wrap gap-2 mb-8">
              <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700">📈 Stocks & ETFs</span>
              <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-primary-100 text-primary-700">💰 Mutual Funds</span>
              <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-700">₿ Cryptocurrencies</span>
              <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">💵 Currencies</span>
              <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-700">🥇 Precious Metals</span>
              <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-teal-100 text-teal-700">🏠 Personal Assets</span>
            </div>

            <ul class="space-y-4 text-gray-600">
              <li class="flex items-start">
                <svg class="w-6 h-6 mr-3 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                <span>Automated price updates for market-traded assets</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 mr-3 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                <span>Custom valuations for houses, cars, art, and more</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 mr-3 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                <span>Unified net worth across all your holdings</span>
              </li>
            </ul>
          </div>
          
          <!-- Visual: Mock Table -->
          <div class="md:w-1/2 relative group">
            <div class="absolute -inset-1 bg-gradient-to-r from-blue-600 to-primary-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
            <div class="relative bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
              <!-- Mock Browser Header -->
              <div class="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-red-400"></div>
                <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div class="w-3 h-3 rounded-full bg-green-400"></div>
                <span class="ml-4 text-xs text-gray-400 font-medium">Portfolio Holdings</span>
              </div>
              
              <!-- Mock Table -->
              <div class="overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gain</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-100">
                    <tr class="hover:bg-gray-50">
                      <td class="px-4 py-3">
                        <div class="flex items-center">
                          <span class="font-semibold text-gray-900 text-sm">AAPL</span>
                          <span class="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Stock</span>
                        </div>
                        <div class="text-xs text-gray-500">Apple Inc.</div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-600">50</td>
                      <td class="px-4 py-3 text-sm text-gray-900 font-medium">$9,425</td>
                      <td class="px-4 py-3 text-sm text-green-600 font-medium">+12.4%</td>
                    </tr>
                    <tr class="hover:bg-gray-50">
                      <td class="px-4 py-3">
                        <div class="flex items-center">
                          <span class="font-semibold text-gray-900 text-sm">BTC</span>
                          <span class="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Crypto</span>
                        </div>
                        <div class="text-xs text-gray-500">Bitcoin</div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-600">0.5</td>
                      <td class="px-4 py-3 text-sm text-gray-900 font-medium">$21,500</td>
                      <td class="px-4 py-3 text-sm text-green-600 font-medium">+8.2%</td>
                    </tr>
                    <tr class="hover:bg-gray-50">
                      <td class="px-4 py-3">
                        <div class="flex items-center">
                          <span class="font-semibold text-gray-900 text-sm">XAU</span>
                          <span class="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Metal</span>
                        </div>
                        <div class="text-xs text-gray-500">Gold (oz)</div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-600">10</td>
                      <td class="px-4 py-3 text-sm text-gray-900 font-medium">$19,850</td>
                      <td class="px-4 py-3 text-sm text-green-600 font-medium">+5.1%</td>
                    </tr>
                    <tr class="hover:bg-gray-50">
                      <td class="px-4 py-3">
                        <div class="flex items-center">
                          <span class="font-semibold text-gray-900 text-sm">🏠</span>
                          <span class="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">Personal</span>
                        </div>
                        <div class="text-xs text-gray-500">Beach House</div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-600">1</td>
                      <td class="px-4 py-3 text-sm text-gray-900 font-medium">$450,000</td>
                      <td class="px-4 py-3 text-sm text-green-600 font-medium">+15.0%</td>
                    </tr>
                    <tr class="hover:bg-gray-50">
                      <td class="px-4 py-3">
                        <div class="flex items-center">
                          <span class="font-semibold text-gray-900 text-sm">EUR</span>
                          <span class="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Currency</span>
                        </div>
                        <div class="text-xs text-gray-500">Euro</div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-600">5,000</td>
                      <td class="px-4 py-3 text-sm text-gray-900 font-medium">$5,425</td>
                      <td class="px-4 py-3 text-sm text-red-600 font-medium">-1.2%</td>
                    </tr>
                  </tbody>
                </table>
                
                <!-- Total Row -->
                <div class="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-between items-center">
                  <span class="text-sm font-medium text-gray-600">Total Portfolio Value</span>
                  <span class="text-lg font-bold text-gray-900">$506,200</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Net Worth & Liabilities Section -->
    <section id="net-worth" class="py-20 bg-slate-50">
      <div class="container mx-auto px-6">
        <div class="flex flex-col md:flex-row items-center gap-16">
          <!-- Visual Mockup (Left) -->
          <div class="md:w-1/2 relative group">
            <div class="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-rose-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
            <div class="relative bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
              <!-- Mock Browser Header -->
              <div class="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-red-400"></div>
                <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div class="w-3 h-3 rounded-full bg-green-400"></div>
                <span class="ml-4 text-xs text-gray-400 font-medium">Net Worth Dashboard</span>
              </div>
              
              <div class="p-6 space-y-6">
                <!-- Net Worth Hero Card with Chart -->
                <div class="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-6 text-white shadow-lg">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium text-primary-200 uppercase tracking-wider">Total Net Worth</span>
                    <span class="px-2 py-1 bg-white/20 rounded-full text-xs font-medium backdrop-blur-sm">Live</span>
                  </div>
                  
                  <!-- Mini Line Chart -->
                  <div class="relative h-24 mt-4">
                    <!-- Grid lines -->
                    <div class="absolute inset-0 flex flex-col justify-between">
                      <div class="border-t border-white/10 w-full"></div>
                      <div class="border-t border-white/10 w-full"></div>
                      <div class="border-t border-white/10 w-full"></div>
                      <div class="border-t border-white/10 w-full"></div>
                    </div>
                    
                    <!-- Chart Area -->
                    <svg class="absolute inset-0 w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
                      <!-- Gradient fill under the line -->
                      <defs>
                        <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" style="stop-color:rgb(167,243,208);stop-opacity:0.4" />
                          <stop offset="100%" style="stop-color:rgb(167,243,208);stop-opacity:0" />
                        </linearGradient>
                      </defs>
                      <!-- Area fill -->
                      <path d="M0,70 L20,65 L40,60 L60,55 L80,50 L100,45 L120,35 L140,30 L160,22 L180,18 L200,10 L200,80 L0,80 Z" fill="url(#chartGradient)" />
                      <!-- Line -->
                      <path d="M0,70 L20,65 L40,60 L60,55 L80,50 L100,45 L120,35 L140,30 L160,22 L180,18 L200,10" fill="none" stroke="rgb(167,243,208)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                      <!-- End dot -->
                      <circle cx="200" cy="10" r="4" fill="rgb(167,243,208)" />
                      <circle cx="200" cy="10" r="6" fill="rgb(167,243,208)" opacity="0.3" />
                    </svg>
                    
                    <!-- Current Value Tooltip -->
                    <div class="absolute -top-1 right-0 transform translate-x-1">
                      <div class="relative">
                        <!-- Tooltip body -->
                        <div class="bg-white rounded-lg px-2.5 py-1 shadow-lg border border-gray-100">
                          <div class="text-sm font-bold bg-gradient-to-r from-primary-600 to-primary-600 bg-clip-text text-transparent">$342,580</div>
                        </div>
                        <!-- Arrow pointer -->
                        <div class="absolute -bottom-1 left-3 w-2 h-2 bg-white border-b border-r border-gray-100 transform rotate-45"></div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- X-axis labels -->
                  <div class="flex justify-between text-xs text-primary-200 mt-2 px-1">
                    <span>Jan</span>
                    <span>Mar</span>
                    <span>May</span>
                    <span>Jul</span>
                    <span>Sep</span>
                    <span>Nov</span>
                  </div>
                </div>

                <!-- Assets vs Liabilities Bar -->
                <div class="space-y-3">
                  <div class="flex justify-between text-sm">
                    <span class="text-emerald-600 font-semibold flex items-center gap-2">
                      <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                      Assets: $486,200
                    </span>
                    <span class="text-rose-600 font-semibold flex items-center gap-2">
                      Liabilities: $143,620
                      <span class="w-2 h-2 rounded-full bg-rose-500"></span>
                    </span>
                  </div>
                  <div class="h-4 rounded-full overflow-hidden flex bg-gray-100 shadow-inner">
                    <div class="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500 flex items-center justify-center" style="width: 77%;">
                      <span class="text-[10px] font-bold text-white drop-shadow">77%</span>
                    </div>
                    <div class="h-full bg-gradient-to-r from-rose-400 to-rose-500 transition-all duration-500 flex items-center justify-center" style="width: 23%;">
                      <span class="text-[10px] font-bold text-white drop-shadow">23%</span>
                    </div>
                  </div>
                </div>

                <!-- Liabilities Breakdown -->
                <div class="space-y-4">
                  <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wider">Active Liabilities</h4>
                  
                  <!-- Mortgage -->
                  <div class="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div class="flex items-center justify-between mb-3">
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                          <svg class="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                          </svg>
                        </div>
                        <div>
                          <p class="font-semibold text-gray-900 text-sm">Home Mortgage</p>
                          <p class="text-xs text-gray-500">15yr Fixed @ 4.5%</p>
                        </div>
                      </div>
                      <div class="text-right">
                        <p class="font-bold text-gray-900">$124,500</p>
                        <p class="text-xs text-gray-500">remaining</p>
                      </div>
                    </div>
                    <div class="space-y-1">
                      <div class="flex justify-between text-xs text-gray-500">
                        <span>Paid off: 58%</span>
                        <span>$172,500 / $297,000</span>
                      </div>
                      <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div class="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full" style="width: 58%;"></div>
                      </div>
                    </div>
                  </div>

                  <!-- Auto Loan -->
                  <div class="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div class="flex items-center justify-between mb-3">
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                          </svg>
                        </div>
                        <div>
                          <p class="font-semibold text-gray-900 text-sm">Auto Loan</p>
                          <p class="text-xs text-gray-500">5yr @ 6.2%</p>
                        </div>
                      </div>
                      <div class="text-right">
                        <p class="font-bold text-gray-900">$14,320</p>
                        <p class="text-xs text-gray-500">remaining</p>
                      </div>
                    </div>
                    <div class="space-y-1">
                      <div class="flex justify-between text-xs text-gray-500">
                        <span>Paid off: 72%</span>
                        <span>$36,680 / $51,000</span>
                      </div>
                      <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div class="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full" style="width: 72%;"></div>
                      </div>
                    </div>
                  </div>

                  <!-- Credit Card -->
                  <div class="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                          </svg>
                        </div>
                        <div>
                          <p class="font-semibold text-gray-900 text-sm">Credit Card</p>
                          <p class="text-xs text-gray-500">Last Month</p>
                        </div>
                      </div>
                      <div class="text-right">
                        <p class="font-bold text-rose-600">$4,800</p>
                        <p class="text-xs text-gray-500">current balance</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Text Content (Right) -->
          <div class="md:w-1/2">
            <div class="bg-gradient-to-r from-emerald-500 to-teal-500 w-12 h-12 rounded-lg flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h2 class="text-3xl font-bold mb-6 text-gray-900">The Full Picture: True Net Worth</h2>
            <p class="text-xl text-gray-600 mb-4 leading-relaxed">
              Tracking assets is only half the story. <span class="font-semibold text-gray-800">Real wealth</span> is what you own minus what you owe.
            </p>
            <p class="text-lg text-gray-500 mb-8 leading-relaxed">
              TrackFolio gives you a complete financial picture by tracking your mortgages, auto loans, credit cards, and any debt—all in one place.
            </p>

            <!-- Key Features -->
            <div class="space-y-5">
              <div class="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div class="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-semibold text-gray-900 mb-1">Smart Amortization</h4>
                  <p class="text-sm text-gray-500">Auto-calculate interest vs. principal for every mortgage and loan payment.</p>
                </div>
              </div>

              <div class="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div class="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-semibold text-gray-900 mb-1">Real-Time Net Worth</h4>
                  <p class="text-sm text-gray-500">Assets minus Liabilities. Updated automatically as you log payments.</p>
                </div>
              </div>

              <div class="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div class="flex-shrink-0 w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/>
                  </svg>
                </div>
                <div>
                  <h4 class="font-semibold text-gray-900 mb-1">Debt Payoff Tracking</h4>
                  <p class="text-sm text-gray-500">Visualize your journey to being debt-free with progress bars and milestones.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>

    <!-- Transaction Tracking Section -->
    <section class="py-20 bg-slate-50">
      <div class="container mx-auto px-6">
        <div class="flex flex-col md:flex-row-reverse items-center gap-16">
          <!-- Text Content -->
          <div class="md:w-1/2">
            <div class="bg-primary-100 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <h2 class="text-3xl font-bold mb-6 text-gray-900">Detailed Transaction History</h2>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Every trade tells a story. Keep a precise record of your buy and sell orders to understand your trading performance over time.
            </p>
            <ul class="space-y-4 text-gray-600">
              <li class="flex items-start">
                <svg class="w-6 h-6 mr-3 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
                <span>Log quantity, price, and date for every move</span>
              </li>
              <li class="flex items-start">
                <svg class="w-6 h-6 mr-3 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>
                <span>Automatic calculation of realized and unrealized gains</span>
              </li>
            </ul>
          </div>
          
          <!-- Visual -->
          <div class="md:w-1/2">
            <div class="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 p-2">
               <img src="@/assets/transaction-table-mock.svg" alt="Detailed Transaction History Table" class="w-full h-auto rounded-lg shadow-sm" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Portfolio Statistics & Analytics Combined Section -->
    <section class="py-20 bg-white overflow-hidden">
      <div class="container mx-auto px-6">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">Deep Dive Analytics</h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto">
            Unlock insights with professional-grade charts and metrics designed to help you optimize your strategy.
          </p>
        </div>

        <!-- Analytics Dashboard Mock -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <!-- Mock Browser Header -->
          <div class="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <div class="w-3 h-3 rounded-full bg-red-400"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
            <div class="w-3 h-3 rounded-full bg-green-400"></div>
            <span class="ml-4 text-xs text-gray-400 font-medium">Portfolio Analysis</span>
          </div>

          <div class="p-6">
            <!-- Top Row: Charts -->
            <div class="grid md:grid-cols-2 gap-6 mb-6">
              <!-- Asset Distribution Chart -->
              <div class="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 class="text-lg font-semibold text-gray-900 mb-6">Asset Distribution</h3>
                <div class="flex items-center justify-between">
                  <!-- Donut Chart -->
                  <div class="relative">
                    <div class="w-40 h-40 rounded-full" style="background: conic-gradient(#3B82F6 0% 35%, #F97316 35% 55%, #FCD34D 55% 70%, #10B981 70% 82%, #14B8A6 82% 100%);">
                      <div class="absolute inset-0 m-auto w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center">
                        <div class="text-center">
                          <div class="text-2xl font-bold text-gray-900">100%</div>
                          <div class="text-xs text-gray-500">Total</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Legend -->
                  <div class="space-y-3 text-sm">
                    <div class="flex items-center gap-3">
                      <span class="w-3 h-3 rounded-sm" style="background: #3B82F6;"></span>
                      <span class="text-gray-600">US Stocks</span>
                      <span class="font-semibold text-gray-900 ml-auto">35%</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <span class="w-3 h-3 rounded-sm" style="background: #F97316;"></span>
                      <span class="text-gray-600">Crypto</span>
                      <span class="font-semibold text-gray-900 ml-auto">20%</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <span class="w-3 h-3 rounded-sm" style="background: #FCD34D;"></span>
                      <span class="text-gray-600">Gold</span>
                      <span class="font-semibold text-gray-900 ml-auto">15%</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <span class="w-3 h-3 rounded-sm" style="background: #10B981;"></span>
                      <span class="text-gray-600">Forex</span>
                      <span class="font-semibold text-gray-900 ml-auto">12%</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <span class="w-3 h-3 rounded-sm" style="background: #14B8A6;"></span>
                      <span class="text-gray-600">Personal</span>
                      <span class="font-semibold text-gray-900 ml-auto">18%</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Performance by Asset Type Chart -->
              <div class="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 class="text-lg font-semibold text-gray-900 mb-6">Performance by Asset Type</h3>
                <!-- Bar Chart -->
                <div class="flex items-end justify-between gap-3" style="height: 180px;">
                  <div class="flex-1 flex flex-col items-center justify-end h-full">
                    <div class="w-full rounded-t-md" style="background: #F97316; height: 85%;"></div>
                    <span class="text-xs text-gray-500 mt-2">Crypto</span>
                    <span class="text-xs font-semibold text-green-600">+42.5%</span>
                  </div>
                  <div class="flex-1 flex flex-col items-center justify-end h-full">
                    <div class="w-full rounded-t-md" style="background: #FCD34D; height: 60%;"></div>
                    <span class="text-xs text-gray-500 mt-2">Gold</span>
                    <span class="text-xs font-semibold text-green-600">+28.3%</span>
                  </div>
                  <div class="flex-1 flex flex-col items-center justify-end h-full">
                    <div class="w-full rounded-t-md" style="background: #3B82F6; height: 45%;"></div>
                    <span class="text-xs text-gray-500 mt-2">US</span>
                    <span class="text-xs font-semibold text-green-600">+18.7%</span>
                  </div>
                  <div class="flex-1 flex flex-col items-center justify-end h-full">
                    <div class="w-full rounded-t-md" style="background: #14B8A6; height: 35%;"></div>
                    <span class="text-xs text-gray-500 mt-2">Personal</span>
                    <span class="text-xs font-semibold text-green-600">+12.0%</span>
                  </div>
                  <div class="flex-1 flex flex-col items-center justify-end h-full">
                    <div class="w-full rounded-t-md" style="background: #10B981; height: 15%;"></div>
                    <span class="text-xs text-gray-500 mt-2">Forex</span>
                    <span class="text-xs font-semibold text-red-500">-2.1%</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bottom Row: Portfolio Statistics -->
            <div class="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Portfolio Statistics</h3>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div class="p-4 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow">
                  <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Portfolio Value</div>
                  <div class="text-xl font-bold text-gray-900 mt-2">$506,200</div>
                </div>
                <div class="p-4 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow">
                  <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Gain/Loss</div>
                  <div class="text-xl font-bold text-green-600 mt-2">+$87,450</div>
                </div>
                <div class="p-4 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow">
                  <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">Overall Performance</div>
                  <div class="text-xl font-bold text-green-600 mt-2">+20.88%</div>
                </div>
                <div class="p-4 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow">
                  <div class="text-xs font-medium text-gray-500 uppercase tracking-wider">Number of Assets</div>
                  <div class="text-xl font-bold text-gray-900 mt-2">24</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Custom Symbols Management Section -->
    <section class="py-20 bg-slate-50">
      <div class="container mx-auto px-6">
        <div class="flex flex-col md:flex-row items-center gap-16">
          <!-- Text Content -->
          <div class="md:w-1/2 order-2 md:order-1">
            <div class="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
              <svg class="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
            </div>
            <h2 class="text-3xl font-bold mb-6 text-gray-900">Track Anything, Literally</h2>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Don't limit yourself to public markets. Add personal assets like real estate, classic cars, watches, or private equity to get your true net worth.
            </p>
            <div class="grid grid-cols-2 gap-4">
              <div class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div class="font-semibold text-gray-900 mb-1">Flexible Types</div>
                <p class="text-sm text-gray-500">Define any asset class you want.</p>
              </div>
              <div class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div class="font-semibold text-gray-900 mb-1">Manual Updates</div>
                <p class="text-sm text-gray-500">Update valuations whenever you want.</p>
              </div>
            </div>
          </div>
          
          <!-- Visual -->
          <div class="md:w-1/2 order-1 md:order-2">
            <div class="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 relative overflow-hidden">
              <div class="absolute top-0 right-0 w-32 h-32 bg-teal-50 rounded-full -mr-16 -mt-16 opacity-50"></div>
              
              <h3 class="font-bold text-lg text-gray-900 mb-4">Your Personal Assets</h3>
              
              <div class="space-y-4">
                <!-- Custom Asset 1 -->
                <div class="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div class="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center text-2xl">🏠</div>
                  <div class="ml-4 flex-1">
                    <div class="flex justify-between items-start">
                      <div>
                        <p class="font-semibold text-gray-900">Downtown Apartment</p>
                        <p class="text-xs text-gray-500">Real Estate</p>
                      </div>
                      <div class="text-right">
                        <p class="font-bold text-gray-900">$450,000</p>
                        <p class="text-xs text-green-600">+$25k (5.8%)</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Custom Asset 2 -->
                <div class="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div class="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center text-2xl">🏎️</div>
                  <div class="ml-4 flex-1">
                    <div class="flex justify-between items-start">
                      <div>
                        <p class="font-semibold text-gray-900">Vintage Porsche</p>
                        <p class="text-xs text-gray-500">Collectible</p>
                      </div>
                      <div class="text-right">
                        <p class="font-bold text-gray-900">$125,000</p>
                        <p class="text-xs text-green-600">+$15k (13.6%)</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Custom Asset 3 -->
                <div class="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div class="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center text-2xl">⌚</div>
                  <div class="ml-4 flex-1">
                    <div class="flex justify-between items-start">
                      <div>
                        <p class="font-semibold text-gray-900">Rolex Submariner</p>
                        <p class="text-xs text-gray-500">Luxury Watch</p>
                      </div>
                      <div class="text-right">
                        <p class="font-bold text-gray-900">$14,500</p>
                        <p class="text-xs text-gray-400">0.0%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="mt-6 text-center">
                <button class="text-primary-600 font-medium text-sm hover:text-primary-700 flex items-center justify-center w-full">
                  + Add New Asset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparison Section: Old Way vs New Way -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-6">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">There's a Better Way to Track Your Wealth</h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto">
            Say goodbye to the chaos. Embrace clarity.
          </p>
        </div>

        <!-- Comparison Grid -->
        <div class="grid md:grid-cols-2 gap-8 lg:gap-12">
          <!-- Left Column: The Struggle -->
          <div class="relative">
            <div class="bg-slate-50 border-2 border-slate-200 rounded-2xl p-8 h-full shadow-sm">
              <!-- Grayscale overlay effect -->
              <div class="absolute inset-0 bg-gradient-to-br from-slate-100/50 to-slate-200/30 rounded-2xl pointer-events-none"></div>
              
              <div class="relative">
                <!-- Header Badge -->
                <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-200 text-gray-600 text-sm font-medium mb-6">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  The Old Way
                </div>

                <h3 class="text-2xl font-bold text-gray-700 mb-4">
                  Stop Juggling Spreadsheets & Multiple Apps
                </h3>
                
                <p class="text-gray-500 mb-8 leading-relaxed">
                  Manual data entry in Excel? Broken formulas? Logging into 5 different accounts just to see your net worth? That belongs in the past.
                </p>

                <!-- Pain Points Container -->
                <div class="bg-gray-100 border border-gray-300 rounded-xl p-6 space-y-4">
                  <div class="flex items-center gap-3 text-gray-600">
                    <span class="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-red-400 font-bold">✕</span>
                    <span class="font-medium">Manual Updates</span>
                  </div>
                  <div class="flex items-center gap-3 text-gray-600">
                    <span class="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-red-400 font-bold">✕</span>
                    <span class="font-medium">Broken Formulas</span>
                  </div>
                  <div class="flex items-center gap-3 text-gray-600">
                    <span class="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-red-400 font-bold">✕</span>
                    <span class="font-medium">Fragmented Data</span>
                  </div>
                  <div class="flex items-center gap-3 text-gray-600">
                    <span class="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-red-400 font-bold">✕</span>
                    <span class="font-medium">Endless Tab Switching</span>
                  </div>
                </div>

                <!-- Decorative "boring" elements -->
                <div class="mt-6 flex gap-2">
                  <div class="h-2 w-16 bg-gray-300 rounded"></div>
                  <div class="h-2 w-24 bg-gray-200 rounded"></div>
                  <div class="h-2 w-12 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: The Solution -->
          <div class="relative">
            <div class="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-8 h-full shadow-xl shadow-primary-500/20 relative overflow-hidden">
              <!-- Decorative background elements -->
              <div class="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
              <div class="absolute bottom-0 left-0 w-48 h-48 bg-cyan-400/20 rounded-full blur-2xl -ml-24 -mb-24"></div>
              
              <div class="relative">
                <!-- Header Badge -->
                <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                  </svg>
                  The TrackFolio Way
                </div>

                <h3 class="text-2xl font-bold text-white mb-4">
                  One Dashboard. Total Clarity.
                </h3>
                
                <p class="text-primary-100 mb-8 leading-relaxed">
                  TrackFolio brings your Stocks, Crypto, and Personal Assets under one roof. Automatic updates, beautiful charts, zero stress.
                </p>

                <!-- Benefits Container -->
                <div class="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 space-y-4">
                  <div class="flex items-center gap-3 text-white">
                    <span class="flex-shrink-0 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-green-400/30">✓</span>
                    <span class="font-medium">Automated Sync</span>
                  </div>
                  <div class="flex items-center gap-3 text-white">
                    <span class="flex-shrink-0 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-green-400/30">✓</span>
                    <span class="font-medium">Unified View</span>
                  </div>
                  <div class="flex items-center gap-3 text-white">
                    <span class="flex-shrink-0 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-green-400/30">✓</span>
                    <span class="font-medium">Automated Analytics</span>
                  </div>
                  <div class="flex items-center gap-3 text-white">
                    <span class="flex-shrink-0 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-green-400/30">✓</span>
                    <span class="font-medium">Track Any Asset Type</span>
                  </div>
                </div>

                <!-- CTA Button -->
                <div class="mt-8">
                  <router-link 
                    to="/portfolios" 
                    class="inline-flex items-center gap-2 bg-white text-primary-600 px-6 py-3 rounded-xl font-semibold hover:bg-primary-50 transition-all duration-200 shadow-lg group"
                  >
                    Start for Free
                    <svg class="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Philosophy Section -->
    <section class="py-16 bg-slate-50">
      <div class="container mx-auto px-6">
        <!-- Section Header -->
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Built for Investors
          </h2>
          <p class="text-gray-600 max-w-2xl mx-auto">
            Escape the noise of second-by-second volatility. Focus on the big picture of your net worth.
          </p>
        </div>

        <!-- Simple 3-Column Grid -->
        <div class="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div class="text-center p-6">
            <div class="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
            </div>
            <h3 class="font-semibold text-gray-900 mb-2">The Macro View</h3>
            <p class="text-sm text-gray-600">Clarity over anxiety. See your entire financial picture in one place.</p>
          </div>

          <div class="text-center p-6">
            <div class="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <h3 class="font-semibold text-gray-900 mb-2">Distraction-Free</h3>
            <p class="text-sm text-gray-600">No flashing lights or panic. Just the data you need to track your worth.</p>
          </div>

          <div class="text-center p-6">
            <div class="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
            </div>
            <h3 class="font-semibold text-gray-900 mb-2">Long-Term Growth</h3>
            <p class="text-sm text-gray-600">Monthly check-ins, not high-frequency trading.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="py-20 bg-white">
      <div class="container mx-auto px-6">
        <h2 class="text-3xl font-bold text-center mb-16 text-gray-900">Simple, Transparent Pricing</h2>
        <div class="grid md:grid-cols-3 gap-8">
          <!-- Basic Plan -->
          <div class="bg-slate-50 rounded-xl shadow-lg p-8 border border-slate-200 hover:shadow-xl transition-shadow">
            <h3 class="text-xl font-semibold mb-2 text-gray-900">Basic</h3>
            <p class="text-gray-500 mb-6 text-sm">Essential tools to get started</p>
            <div class="mb-6">
              <span class="text-4xl font-bold text-gray-900">Free</span>
            </div>
            <ul class="space-y-4 mb-8">
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                1 Portfolio
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                50 Transactions
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                1 Personal Asset
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                1 Liability
              </li>
            </ul>
            <router-link to="/portfolios" class="block w-full bg-gray-100 text-gray-900 font-semibold py-3 rounded-lg hover:bg-gray-200 transition-colors text-center">
              Get Started
            </router-link>
          </div>

          <!-- Pro Plan -->
          <div class="bg-white rounded-xl shadow-xl p-8 border-2 border-primary-600 relative transform scale-105 z-10">
            <div class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <span class="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium uppercase tracking-wide">Most Popular</span>
            </div>
            <h3 class="text-xl font-semibold mb-2 text-gray-900">Pro</h3>
            <p class="text-gray-500 mb-6 text-sm">For growing investors</p>
            <div class="mb-6 flex items-baseline">
              <span class="text-4xl font-bold text-gray-900">$4.99</span>
              <span class="text-gray-500 ml-2">/month</span>
            </div>
            <ul class="space-y-4 mb-8">
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                10 Portfolios
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                2,000 Transactions
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                Unlimited Personal Assets
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                10 Liabilities
              </li>
            </ul>
            <router-link to="/portfolios" class="block w-full bg-primary-600 text-white font-semibold py-3 rounded-lg hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30 text-center">
              Get Pro
            </router-link>
          </div>

          <!-- Trader Plan -->
          <div class="bg-slate-50 rounded-xl shadow-lg p-8 border border-slate-200 hover:shadow-xl transition-shadow">
            <h3 class="text-xl font-semibold mb-2 text-gray-900">Wealth</h3>
            <p class="text-gray-500 mb-6 text-sm">For managing significant assets and complex portfolios</p>
            <div class="mb-6 flex items-baseline">
              <span class="text-4xl font-bold text-gray-900">$12.99</span>
              <span class="text-gray-500 ml-2">/month</span>
            </div>
            <ul class="space-y-4 mb-8">
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                Unlimited Portfolios
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                Unlimited Transactions
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                Unlimited Liabilities
              </li>
              <li class="flex items-center text-gray-600">
                <svg class="w-5 h-5 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                Priority Feature Requests
              </li>
            </ul>
            <router-link to="/portfolios" class="block w-full bg-white text-primary-600 font-semibold py-3 rounded-lg border border-primary-600 hover:bg-primary-50 transition-colors text-center">
              Get Wealth
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action -->
    <section class="py-20 bg-primary-600 text-white">
      <div class="container mx-auto px-6 text-center">
        <h2 class="text-3xl font-bold mb-8">Ready to Take Control of Your Investments?</h2>
        <router-link to="/portfolios" class="bg-white text-primary-600 px-8 py-3 rounded-full font-semibold text-lg hover:bg-opacity-90 transition duration-150 inline-block">
          Get Started Now
        </router-link>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gray-900 text-white py-12">
      <div class="container mx-auto px-6">
        <!-- Footer Content -->
        <div class="flex flex-col md:flex-row justify-between items-center mb-8">
          <!-- Logo & Tagline -->
          <div class="flex items-center gap-3 mb-6 md:mb-0">
            <img src="@/assets/logo.svg" alt="Logo" class="h-8 w-auto" />
            <span class="text-lg font-bold tracking-tight">TrackFolio</span>
          </div>
          
          <!-- Footer Links -->
          <div class="flex items-center gap-8 text-sm text-gray-400">
            <a href="#" class="hover:text-white transition-colors">Home</a>
            <a href="#features" class="hover:text-white transition-colors">Features</a>
            <a href="#pricing" class="hover:text-white transition-colors">Pricing</a>
          </div>
        </div>

        <!-- Divider -->
        <div class="border-t border-gray-800 pt-8">
          <!-- Copyright -->
          <p class="text-sm text-gray-500 text-center mb-6">
            © {{ new Date().getFullYear() }} TrackFolio. All rights reserved.
          </p>
          
          <!-- Disclaimer -->
          <p class="text-xs text-gray-500 text-center max-w-4xl mx-auto leading-relaxed">
            <span class="font-medium text-gray-400">Disclaimer:</span> Market data is provided for informational purposes only and is not intended for trading purposes or financial advice. Prices may be delayed up to 15 minutes. TrackFolio is a portfolio tracking tool, not a brokerage or trading platform. Always verify prices with your broker before executing trades.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'LandingPage',
  data() {
    return {
      mobileMenuOpen: false
    }
  }
}
</script> 